import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { compensationApi, cycleApi, employeeApi } from '../../services/api';
import {
  PageHeader,
  StatCard,
  EmptyState,
  Spinner,
  Modal,
  ConfirmModal,
  BandBadge,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  BanknotesIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  PlusIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const CURRENCIES   = ['USD', 'EUR', 'GBP', 'INR'];
const RATING_KEYS  = ['X', 'E+', 'E', 'E-'];
const DEFAULT_MERIT = {
  X:    { min: 10, max: 20 },
  'E+': { min: 5,  max: 10 },
  E:    { min: 2,  max: 5  },
  'E-': { min: 0,  max: 0  },
};

const formatMoney = (v, currency = 'USD') => {
  if (v === null || v === undefined || v === '') return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return `${currency} ${Number(v).toLocaleString()}`;
  }
};

const compaRatio = (base, mid) => {
  if (!base || !mid) return null;
  return Math.round((Number(base) / Number(mid)) * 100);
};

const compaColor = (r) => {
  if (r === null || r === undefined) return 'bg-gray-100 text-gray-600 border-gray-200';
  if (r < 90)  return 'bg-red-50   text-red-700   border-red-200';
  if (r > 110) return 'bg-blue-50  text-blue-700  border-blue-200';
  return 'bg-green-50 text-green-700 border-green-200';
};

// ─────────────────────────────────────────────────────────────
// Salary Band form
// ─────────────────────────────────────────────────────────────
function BandForm({ initial, onCancel, onSubmit, saving }) {
  const [role, setRole]   = useState(initial?.role || '');
  const [band, setBand]   = useState(initial?.band || 'E');
  const [minS, setMinS]   = useState(initial?.min_salary ?? '');
  const [midS, setMidS]   = useState(initial?.mid_salary ?? '');
  const [maxS, setMaxS]   = useState(initial?.max_salary ?? '');
  const [curr, setCurr]   = useState(initial?.currency || 'USD');
  const [merit, setMerit] = useState(() => {
    const base = initial?.merit_increase_by_rating || DEFAULT_MERIT;
    const m = {};
    RATING_KEYS.forEach(k => {
      m[k] = {
        min: base?.[k]?.min ?? DEFAULT_MERIT[k].min,
        max: base?.[k]?.max ?? DEFAULT_MERIT[k].max,
      };
    });
    return m;
  });

  const valid =
    role.trim() &&
    band.trim() &&
    Number(minS) > 0 &&
    Number(midS) >= Number(minS) &&
    Number(maxS) >= Number(midS);

  const submit = () => {
    onSubmit({
      role: role.trim(),
      band: band.trim(),
      min_salary: Number(minS),
      mid_salary: Number(midS),
      max_salary: Number(maxS),
      currency: curr,
      merit_increase_by_rating: merit,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Role</label>
          <input className="input" value={role} onChange={e => setRole(e.target.value)} placeholder="Software Engineer" />
        </div>
        <div>
          <label className="label">Band</label>
          <select className="input" value={band} onChange={e => setBand(e.target.value)}>
            {['L1','L2','L3','L4','L5','L6','L7','X','E+','E','E-'].map(b => <option key={b}>{b}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="label">Min</label>
          <input type="number" className="input" value={minS} onChange={e => setMinS(e.target.value)} />
        </div>
        <div>
          <label className="label">Mid</label>
          <input type="number" className="input" value={midS} onChange={e => setMidS(e.target.value)} />
        </div>
        <div>
          <label className="label">Max</label>
          <input type="number" className="input" value={maxS} onChange={e => setMaxS(e.target.value)} />
        </div>
        <div>
          <label className="label">Currency</label>
          <select className="input" value={curr} onChange={e => setCurr(e.target.value)}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="label mb-2">Merit increase grid (%)</label>
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-3 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span>Rating</span><span>Min %</span><span>Max %</span>
          </div>
          {RATING_KEYS.map(k => (
            <div key={k} className="grid grid-cols-3 items-center px-3 py-2 border-t border-gray-100">
              <BandBadge band={k} />
              <input type="number" className="input !py-1 !text-sm w-24"
                value={merit[k].min}
                onChange={e => setMerit({ ...merit, [k]: { ...merit[k], min: Number(e.target.value) } })} />
              <input type="number" className="input !py-1 !text-sm w-24"
                value={merit[k].max}
                onChange={e => setMerit({ ...merit, [k]: { ...merit[k], max: Number(e.target.value) } })} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button onClick={submit} disabled={!valid || saving} className="btn-primary disabled:opacity-40">
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create band'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Edit base salary
// ─────────────────────────────────────────────────────────────
function EditBaseForm({ row, onCancel, onSubmit, saving }) {
  const [value, setValue] = useState(row.base_salary ?? '');
  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 space-y-1">
        <div className="flex justify-between"><span>Employee</span><span className="font-medium">{row.employee_name}</span></div>
        <div className="flex justify-between"><span>Role / Band</span><span className="font-medium capitalize">{row.role} / {row.band}</span></div>
        <div className="flex justify-between"><span>Current base</span><span className="font-medium">{formatMoney(row.base_salary)}</span></div>
      </div>
      <div>
        <label className="label">New base salary</label>
        <input type="number" className="input" value={value} onChange={e => setValue(e.target.value)} />
        <p className="text-xs text-gray-500 mt-1">Server will reject values outside the approved band range.</p>
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button disabled={!value || saving} className="btn-primary disabled:opacity-40"
          onClick={() => onSubmit(Number(value))}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Salary Bands tab
// ─────────────────────────────────────────────────────────────
function SalaryBandsTab() {
  const qc = useQueryClient();
  const [editing, setEditing]     = useState(null); // band object or 'new'
  const [archiving, setArchiving] = useState(null);

  const { data: bands = [], isLoading } = useQuery({
    queryKey: ['salary-bands'],
    queryFn:  () => compensationApi.bands().then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: data => compensationApi.createBand(data),
    onSuccess:  () => { toast.success('Band created'); qc.invalidateQueries(['salary-bands']); setEditing(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to create band'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => compensationApi.updateBand(id, data),
    onSuccess:  () => { toast.success('Band updated'); qc.invalidateQueries(['salary-bands']); setEditing(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to update'),
  });
  const deleteMut = useMutation({
    mutationFn: id => compensationApi.deleteBand(id),
    onSuccess:  () => { toast.success('Band archived'); qc.invalidateQueries(['salary-bands']); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to archive'),
  });

  if (isLoading) return <div className="flex justify-center p-10"><Spinner /></div>;

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing('new')} className="btn-primary">
          <PlusIcon className="w-4 h-4" />New band
        </button>
      </div>

      {bands.length === 0 ? (
        <EmptyState
          title="No salary bands yet"
          description="Define a band per role to enable merit planning and compa-ratio analysis."
          action={<button onClick={() => setEditing('new')} className="btn-primary"><PlusIcon className="w-4 h-4" />Create first band</button>}
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['Role','Band','Min','Mid','Max','Currency','Merit by rating','Actions'].map(h =>
                  <th key={h} className="th">{h}</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {bands.map(b => (
                <tr key={b.id} className="tr-hover">
                  <td className="td font-medium text-gray-900 capitalize">{b.role}</td>
                  <td className="td"><BandBadge band={b.band} /></td>
                  <td className="td font-mono text-sm">{formatMoney(b.min_salary, b.currency)}</td>
                  <td className="td font-mono text-sm">{formatMoney(b.mid_salary, b.currency)}</td>
                  <td className="td font-mono text-sm">{formatMoney(b.max_salary, b.currency)}</td>
                  <td className="td text-xs text-gray-600">{b.currency}</td>
                  <td className="td">
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      {RATING_KEYS.map(k => {
                        const m = b.merit_increase_by_rating?.[k] || { min: 0, max: 0 };
                        return (
                          <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 rounded border border-gray-200">
                            <span className="font-semibold">{k}</span>
                            <span className="text-gray-500">{m.min}–{m.max}%</span>
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="td">
                    <div className="flex gap-3">
                      <button onClick={() => setEditing(b)} className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1">
                        <PencilSquareIcon className="w-3.5 h-3.5" />Edit
                      </button>
                      <button onClick={() => setArchiving(b)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                        <ArchiveBoxIcon className="w-3.5 h-3.5" />Archive
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Create salary band' : `Edit band — ${editing?.role || ''}`}
        size="lg"
      >
        {editing && (
          <BandForm
            initial={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSubmit={data => {
              if (editing === 'new') createMut.mutate(data);
              else updateMut.mutate({ id: editing.id, data });
            }}
            saving={createMut.isPending || updateMut.isPending}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!archiving}
        onClose={() => setArchiving(null)}
        onConfirm={() => archiving && deleteMut.mutate(archiving.id)}
        title="Archive band?"
        message={`This will archive the ${archiving?.role || ''} / ${archiving?.band || ''} band. Existing employees will remain untouched.`}
        danger
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Merit Cycle tab
// ─────────────────────────────────────────────────────────────
function MeritCycleTab() {
  const qc = useQueryClient();
  const [cycleId, setCycleId]       = useState('');
  const [editingRow, setEditingRow] = useState(null);

  const { data: cycles = [] } = useQuery({
    queryKey: ['cycles'],
    queryFn:  () => cycleApi.list().then(r => r.data),
  });

  useEffect(() => {
    if (!cycleId && cycles.length) {
      const preferred =
        cycles.find(c => c.status === 'locked' || c.status === 'published') ||
        cycles[0];
      if (preferred) setCycleId(preferred.id);
    }
  }, [cycles, cycleId]);

  const { data, isLoading } = useQuery({
    queryKey: ['merit-cycle', cycleId],
    queryFn:  () => compensationApi.meritCycle(cycleId).then(r => r.data),
    enabled:  !!cycleId,
  });

  const updateBaseMut = useMutation({
    mutationFn: ({ eid, base_salary }) => compensationApi.update(eid, { base_salary }),
    onSuccess:  () => { toast.success('Base salary updated'); qc.invalidateQueries(['merit-cycle', cycleId]); setEditingRow(null); },
    onError:    e => {
      const status = e.response?.status;
      const msg    = e.response?.data?.message || 'Failed to update base';
      if (status === 422) toast.error(`Outside band range: ${msg}`);
      else toast.error(msg);
    },
  });

  const rows     = data?.rows || [];
  const byDept   = data?.by_department || [];
  const totalRec = data?.total_recommended || 0;

  const avgCompa = useMemo(() => {
    if (!rows.length) return 0;
    const sum = rows.reduce((s, r) => s + (Number(r.compa_ratio) || 0), 0);
    return Math.round(sum / rows.length);
  }, [rows]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['Employee','Role','Band','Rating','Base','Compa-ratio','Merit min %','Merit max %','Recommended'];
    const lines  = [header.join(',')];
    rows.forEach(r => {
      lines.push([
        `"${(r.employee_name || '').replace(/"/g, '""')}"`,
        r.role,
        r.band,
        r.rating,
        r.base_salary,
        r.compa_ratio,
        r.merit_min_pct,
        r.merit_max_pct,
        r.recommended_amount,
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `merit-cycle-${cycleId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Cycle:</label>
          <select className="input w-64 text-sm" value={cycleId} onChange={e => setCycleId(e.target.value)}>
            <option value="">Select cycle…</option>
            {cycles.map(c => (
              <option key={c.id} value={c.id}>{c.name} · {c.status}</option>
            ))}
          </select>
        </div>
        <button onClick={exportCsv} disabled={!rows.length} className="btn-secondary disabled:opacity-40">
          <ArrowDownTrayIcon className="w-4 h-4" />Export CSV
        </button>
      </div>

      {!cycleId ? (
        <EmptyState title="Select a cycle" description="Choose a review cycle above to view merit recommendations." />
      ) : isLoading ? (
        <div className="flex justify-center p-10"><Spinner /></div>
      ) : !rows.length ? (
        <EmptyState title="No merit data" description="This cycle has no compensation recommendations yet. Lock ratings to generate the merit plan." />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <StatCard title="Total recommended" value={formatMoney(totalRec)} subtitle="Across all employees" color="green"  icon={CurrencyDollarIcon} />
            <StatCard title="Employees"         value={rows.length}           subtitle="In the merit plan"  color="blue"   icon={UsersIcon} />
            <StatCard title="Avg compa-ratio"   value={`${avgCompa}%`}        subtitle="Base vs band mid"    color="purple" icon={ScaleIcon} />
          </div>

          <div className="table-wrap mb-6">
            <table className="table">
              <thead>
                <tr>
                  {['Employee','Role / Band','Rating','Base','Compa-ratio','Merit range','Recommended','Actions'].map(h =>
                    <th key={h} className="th">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.employee_id} className="tr-hover">
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                          {r.employee_name?.[0] || '?'}
                        </div>
                        <span className="text-sm font-medium text-gray-900">{r.employee_name}</span>
                      </div>
                    </td>
                    <td className="td text-xs capitalize text-gray-600">{r.role?.replace('_',' ')} / {r.band}</td>
                    <td className="td"><BandBadge band={r.rating} /></td>
                    <td className="td font-mono text-sm">{formatMoney(r.base_salary)}</td>
                    <td className="td">
                      <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs border font-medium', compaColor(r.compa_ratio))}>
                        {r.compa_ratio}%
                      </span>
                    </td>
                    <td className="td text-xs text-gray-700">{r.merit_min_pct}–{r.merit_max_pct}%</td>
                    <td className="td font-mono text-sm font-semibold text-gray-900">{formatMoney(r.recommended_amount)}</td>
                    <td className="td">
                      <button onClick={() => setEditingRow(r)} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
                        Edit base
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {byDept.length > 0 && (
            <div className="card p-5">
              <h3 className="section-title mb-4">Recommended spend by department</h3>
              <div className="space-y-2">
                {byDept.map(d => {
                  const pct = totalRec ? Math.round((d.total / totalRec) * 100) : 0;
                  return (
                    <div key={d.department_id} className="flex items-center gap-3">
                      <div className="w-40 text-sm text-gray-700 truncate">{d.department_name || d.department_id}</div>
                      <div className="flex-1 bg-gray-100 h-3 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-52 text-right text-xs text-gray-600 font-mono">
                        {formatMoney(d.total)} · {d.count} emp · {pct}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <Modal open={!!editingRow} onClose={() => setEditingRow(null)} title="Edit base salary" size="sm">
        {editingRow && (
          <EditBaseForm
            row={editingRow}
            onCancel={() => setEditingRow(null)}
            onSubmit={v => updateBaseMut.mutate({ eid: editingRow.employee_id, base_salary: v })}
            saving={updateBaseMut.isPending}
          />
        )}
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Compa-Ratio Audit tab
// ─────────────────────────────────────────────────────────────
function CompaAuditTab() {
  const { data: employees = [], isLoading: empLoading } = useQuery({
    queryKey: ['employees'],
    queryFn:  () => employeeApi.list().then(r => r.data),
  });
  const { data: bands = [], isLoading: bandLoading } = useQuery({
    queryKey: ['salary-bands'],
    queryFn:  () => compensationApi.bands().then(r => r.data),
  });

  const rows = useMemo(() => {
    const bandMap = new Map();
    bands.forEach(b => bandMap.set(`${b.role}:${b.band}`, b));
    return employees.map(e => {
      const band = bandMap.get(`${e.role}:${e.band}`);
      const base = Number(e.base_salary) || 0;
      const mid  = band ? Number(band.mid_salary) : 0;
      const r    = compaRatio(base, mid);
      let flag = null;
      if (r !== null) {
        if (r < 85)       flag = { type: 'risk', label: 'Flight risk', color: 'bg-red-50 text-red-700 border-red-200' };
        else if (r > 120) flag = { type: 'over', label: 'Over-paid',   color: 'bg-blue-50 text-blue-700 border-blue-200' };
      }
      return { ...e, _band: band, _compa: r, _flag: flag, _mid: mid, _base: base };
    });
  }, [employees, bands]);

  const outliers = rows.filter(r => r._flag).length;

  if (empLoading || bandLoading) return <div className="flex justify-center p-10"><Spinner /></div>;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Showing {rows.length} employees · <span className="font-semibold text-gray-800">{outliers}</span> outliers
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No employees to audit" description="Import or sync your workforce to compute compa-ratios." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['Employee','Role / Band','Base','Mid of band','Compa-ratio','Flag'].map(h =>
                  <th key={h} className="th">{h}</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className={clsx('tr-hover', r._flag && 'bg-amber-50/30')}>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                        {r.name?.[0] || '?'}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{r.name}</span>
                    </div>
                  </td>
                  <td className="td text-xs capitalize text-gray-600">{r.role?.replace('_',' ')} / {r.band}</td>
                  <td className="td font-mono text-sm">{formatMoney(r._base)}</td>
                  <td className="td font-mono text-sm text-gray-500">
                    {r._mid ? formatMoney(r._mid) : <span className="italic text-gray-400">no band</span>}
                  </td>
                  <td className="td">
                    {r._compa !== null ? (
                      <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs border font-medium', compaColor(r._compa))}>
                        {r._compa}%
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="td">
                    {r._flag ? (
                      <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border', r._flag.color)}>
                        <ExclamationTriangleIcon className="w-3 h-3" />{r._flag.label}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function Compensation() {
  const [tab, setTab] = useState('bands');

  const { data: bands = [] }     = useQuery({ queryKey: ['salary-bands'], queryFn: () => compensationApi.bands().then(r => r.data) });
  const { data: cycles = [] }    = useQuery({ queryKey: ['cycles'],       queryFn: () => cycleApi.list().then(r => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ['employees'],    queryFn: () => employeeApi.list().then(r => r.data) });

  const activeCycles = cycles.filter(
    c => c.status === 'active' || c.status === 'locked' || c.status === 'published'
  ).length;

  const pendingReviews = cycles
    .filter(c => c.status === 'locked' || c.status === 'published')
    .reduce((s, c) => s + (c.employee_count || 0), 0);

  const outliers = useMemo(() => {
    const bandMap = new Map();
    bands.forEach(b => bandMap.set(`${b.role}:${b.band}`, b));
    return employees.filter(e => {
      const b   = bandMap.get(`${e.role}:${e.band}`);
      const mid = b ? Number(b.mid_salary) : 0;
      const r   = compaRatio(Number(e.base_salary), mid);
      return r !== null && (r < 85 || r > 120);
    }).length;
  }, [bands, employees]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Compensation"
        subtitle="Manage salary bands, run merit cycles, and audit pay equity across the org."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Bands defined"         value={bands.length}   color="blue"  icon={BanknotesIcon} />
        <StatCard title="Active cycles"         value={activeCycles}   color="green" icon={ChartBarIcon} />
        <StatCard title="Pending merit reviews" value={pendingReviews} color="amber" icon={UsersIcon} />
        <StatCard title="Compa-ratio outliers"  value={outliers}       color="red"   icon={ExclamationTriangleIcon} />
      </div>

      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {[
          ['bands', 'Salary Bands'],
          ['merit', 'Merit Cycle'],
          ['audit', 'Compa-Ratio Audit'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >{l}</button>
        ))}
      </div>

      {tab === 'bands' && <SalaryBandsTab />}
      {tab === 'merit' && <MeritCycleTab />}
      {tab === 'audit' && <CompaAuditTab />}
    </div>
  );
}
